import { askAgent, streamAgent } from "@vida/oceany-agent-client";
import {
  newId,
  type SurfDeck,
  type SurfScriptReference,
  type SurfSlide,
  setSpeakerScript,
} from "@vida/surf-core";
import { getIdToken } from "../auth";
import { APP_ID, getAppsyncEndpoint } from "../config";
import { createOceanSignedFetch } from "./oceanSignedFetch";
import {
  getOceanAgentStreamUrl,
  getSurfResearcherAgentId,
  isSpeakerResearchConfigured,
} from "./surfOceanConfig";

export { isSpeakerResearchConfigured } from "./surfOceanConfig";

export type SpeakerResearchProgress = {
  kind: "step" | "delta" | "meta";
  label: string;
};

export type SpeakerResearchResult = {
  references: SurfScriptReference[];
  jobId?: string;
  outputText?: string;
};

function slideTitle(slide: SurfSlide): string {
  return (
    slide.layers.find((l) => l.type === "text" && l.textRole === "title")?.content?.trim() ??
    "Slide"
  );
}

function buildResearchGoal(deck: SurfDeck, slide: SurfSlide): string {
  const title = slideTitle(slide);
  const themes = deck.deckContext?.themes?.join(", ") ?? deck.title;
  return (
    `Trova fonti autorevoli e citazioni per il discorso della slide "${title}" ` +
    `sul tema ${themes}. Restituisci 3–6 riferimenti con titolo, URL e snippet breve in JSON: ` +
    `{"references":[{"title":"...","url":"...","snippet":"..."}]}`
  );
}

function buildContextJson(deck: SurfDeck, slide: SurfSlide) {
  const section = deck.outline?.find((s) => s.id === slide.outlineSectionId);
  return {
    source: "surf",
    deckId: deck.id,
    slideId: slide.id,
    slideTitle: slideTitle(slide),
    themes: deck.deckContext?.themes ?? [],
    bullets: section?.bullets ?? [],
  };
}

function parseReferencesFromText(
  text: string,
  jobId?: string,
): SurfScriptReference[] {
  const trimmed = text.trim();
  if (!trimmed) return [];
  try {
    const jsonStart = trimmed.indexOf("{");
    const jsonEnd = trimmed.lastIndexOf("}");
    if (jsonStart >= 0 && jsonEnd > jsonStart) {
      const parsed = JSON.parse(trimmed.slice(jsonStart, jsonEnd + 1)) as {
        references?: Array<Record<string, unknown>>;
      };
      if (Array.isArray(parsed.references)) {
        const refs: SurfScriptReference[] = [];
        for (const r of parsed.references) {
          const title = String(r.title ?? "").trim();
          if (!title) continue;
          refs.push({
            id: newId(),
            title,
            url: typeof r.url === "string" ? r.url : undefined,
            snippet: typeof r.snippet === "string" ? r.snippet : undefined,
            source: "agent",
            jobId,
          });
        }
        return refs.slice(0, 8);
      }
    }
  } catch {
    /* fallback below */
  }
  const lines = trimmed.split("\n").filter((l) => l.trim());
  return lines.slice(0, 6).map((line) => ({
    id: newId(),
    title: line.replace(/^[-*\d.]+\s*/, "").slice(0, 200),
    source: "agent" as const,
    jobId,
  }));
}

export async function researchSpeakerReferences(
  deck: SurfDeck,
  slideId: string,
  onProgress?: (p: SpeakerResearchProgress) => void,
): Promise<SpeakerResearchResult> {
  if (!isSpeakerResearchConfigured()) {
    throw new Error(
      "Ricerca riferimenti non configurata (surf_researcher_agent_id / ocean stream).",
    );
  }
  const slide = deck.slides.find((s) => s.id === slideId);
  if (!slide) throw new Error("Slide non trovata.");

  const goal = buildResearchGoal(deck, slide);
  const contextJson = buildContextJson(deck, slide);
  const streamUrl = getOceanAgentStreamUrl();
  const agentId = getSurfResearcherAgentId();
  const jwt = await getIdToken();
  const fetchFn = await createOceanSignedFetch();

  let jobId = "";
  let outputText = "";

  try {
    for await (const event of streamAgent({
      streamUrl,
      jwt,
      appId: APP_ID,
      agentId,
      goal,
      contextJson,
      fetchFn,
    })) {
      if (event.type === "meta") {
        jobId = event.jobId;
        onProgress?.({ kind: "meta", label: `Missione ${event.jobId.slice(0, 8)}…` });
      } else if (event.type === "step") {
        onProgress?.({
          kind: "step",
          label: event.summary?.trim() || event.kind,
        });
      } else if (event.type === "delta") {
        outputText += event.text;
        onProgress?.({ kind: "delta", label: "Ricerca in corso…" });
      } else if (event.type === "done") {
        jobId = event.jobId || jobId;
        outputText = event.outputText?.trim() || outputText;
      } else if (event.type === "error") {
        throw new Error(event.message || "Errore agente ricerca.");
      }
    }
  } catch {
    const asyncResult = await askAgent({
      appsyncUrl: getAppsyncEndpoint(),
      jwt,
      appId: APP_ID,
      agentId,
      goal,
      contextJson,
    });
    jobId = asyncResult.jobId;
    outputText = asyncResult.outputText ?? "";
  }

  const references = parseReferencesFromText(outputText, jobId);
  return { references, jobId, outputText };
}

export function applyReferencesToDeck(
  deck: SurfDeck,
  slideId: string,
  references: SurfScriptReference[],
): SurfDeck {
  const slide = deck.slides.find((s) => s.id === slideId);
  const prev = slide?.speakerScript?.references ?? [];
  return setSpeakerScript(deck, slideId, {
    references: [...prev, ...references],
    status: slide?.speakerScript?.text?.trim() ? slide.speakerScript.status : "empty",
  });
}

export function appendCitationToScript(deck: SurfDeck, slideId: string, ref: SurfScriptReference): SurfDeck {
  const slide = deck.slides.find((s) => s.id === slideId);
  const prev = slide?.speakerScript?.text?.trim() ?? "";
  const cite = ref.url ? `${ref.title} (${ref.url})` : ref.title;
  const next = prev ? `${prev}\n\n[Citazione: ${cite}]` : `[Citazione: ${cite}]`;
  return setSpeakerScript(deck, slideId, { text: next, status: "edited" });
}

export function removeReferenceFromDeck(
  deck: SurfDeck,
  slideId: string,
  refId: string,
): SurfDeck {
  const slide = deck.slides.find((s) => s.id === slideId);
  const refs = slide?.speakerScript?.references?.filter((r) => r.id !== refId) ?? [];
  return setSpeakerScript(deck, slideId, { references: refs });
}
